import { useId, useMemo, useState } from 'react'
import type { Amount, LedgerRow } from './lib/analysis'
import { findMerchants, monthlyTotals } from './lib/merchants'
import { monthLabel, niceMax, useHover, useWidth } from './charts'
import type { Filter } from './Transactions'

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
const whole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

/** Every transaction filter covers all history and every account. */
export const merchantFilter = (merchant: string): Filter => ({ title: merchant, merchant })
const textFilter = (query: string): Filter => ({ title: `Containing “${query}”`, query })

/**
 * The Spending tab's merchant search: type part of a name ("mcdonalds",
 * "portland general") to chart money out by month as you type, and pick a
 * merchant to list every transaction there. Covers every month on file,
 * whatever range the other charts show.
 */
export function MerchantSearch({ ledger, onOpen }: { ledger: LedgerRow[]; onOpen: (f: Filter) => void }) {
  const [text, setText] = useState('')
  const [at, setAt] = useState(0)
  const list = useId()
  const hits = useMemo(() => findMerchants(ledger, text), [ledger, text])
  const totals = useMemo(() => monthlyTotals(ledger, text), [ledger, text])
  const query = text.trim()
  // The merchants, then "everything containing the text" as the last choice.
  const choices: Filter[] = query && totals ? [...hits.map((h) => merchantFilter(h.merchant)), textFilter(query)] : []
  return (
    <section className="section merchant-search">
      <div className="row">
        <h2>Find a merchant</h2>
        <span className="label">All months on file</span>
      </div>
      <input
        type="search"
        role="combobox"
        aria-label="Find a merchant"
        aria-expanded={choices.length > 0}
        aria-controls={list}
        aria-activedescendant={choices.length ? `${list}-${at}` : undefined}
        placeholder="Part of a name, e.g. mcdonalds"
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setAt(0)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setAt((i) => Math.min(i + 1, choices.length - 1))
          else if (e.key === 'ArrowUp') setAt((i) => Math.max(i - 1, 0))
          else if (e.key === 'Enter' && choices[at]) onOpen(choices[at])
          else return
          e.preventDefault()
        }}
      />
      {query && !totals && <p className="muted">No transactions contain “{query}”.</p>}
      {totals && (
        <>
          <MonthChart query={query} months={totals.months} net={totals.net} count={totals.count} onMonth={(month) => onOpen({ title: `Containing “${query}”, ${month}`, query, month })} />
          <ul id={list} role="listbox" aria-label="Matching merchants" className="merchant-choices">
            {choices.map((f, i) => {
              const h = hits[i]
              return (
                <li key={f.title} id={`${list}-${i}`} role="option" aria-selected={i === at} onClick={() => onOpen(f)} onMouseEnter={() => setAt(i)}>
                  {h ? (
                    <>
                      <span className="name">{h.merchant}</span>
                      <span className="muted num">{h.count}× · {money.format(Math.abs(h.net))} · latest {h.last}</span>
                    </>
                  ) : (
                    <span className="name">All transactions containing “{query}”</span>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </section>
  )
}

const H = 180
const TOP = 20
const AXIS = 22

/**
 * Money out per month for the typed text, redrawn on every keystroke. Amounts
 * are printed over the bars (or in the line above on hover), so the bars'
 * colour carries nothing on its own. Clicking a bar lists that month.
 */
function MonthChart({ query, months, net, count, onMonth }: { query: string; months: Amount[]; net: number; count: number; onMonth: (month: string) => void }) {
  const [ref, W] = useWidth()
  const [hover, setHover] = useHover(months.length)
  const max = niceMax(Math.max(...months.map((m) => m.total)))
  const step = W / months.length
  const bar = Math.max(2, Math.min(56, step * 0.6))
  const y = (v: number) => TOP + (H - TOP - AXIS) * (1 - Math.max(0, v) / max)
  const h = hover === undefined ? undefined : months[hover]
  // Amounts over every bar while they fit; past that, hover shows them.
  const printed = step >= 52
  return (
    <figure className="chart month-chart" ref={ref}>
      <figcaption className="small-inline">
        {h ? (
          <><b className="num">{h.label}</b> · <span className="num">{money.format(h.total)}</span> · {h.count} transaction{h.count === 1 ? '' : 's'}{h.count ? ' · click to list' : ''}</>
        ) : (
          <>“{query}”: <span className="num">{money.format(net)}</span> over {count} transaction{count === 1 ? '' : 's'}</>
        )}
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Money out per month for ${query}`} onMouseLeave={() => setHover(undefined)}>
        <line className="axis" x1={0} x2={W} y1={H - AXIS} y2={H - AXIS} />
        {months.map((m, i) => {
          const cx = step * i + step / 2
          const label = monthLabel(m.label, i, months.length)
          return (
            <g key={m.label} className={m.count ? 'clickable' : undefined} onMouseEnter={() => setHover(i)} onClick={() => m.count && onMonth(m.label)}>
              <rect className="hit" x={step * i} y={0} width={step} height={H} />
              {m.total > 0 && <rect className={`month-bar${hover === i ? ' on' : ''}`} x={cx - bar / 2} y={y(m.total)} width={bar} height={H - AXIS - y(m.total)} />}
              {printed && m.total > 0 && (
                <text className="value" x={cx} y={y(m.total) - 5} textAnchor="middle">{whole.format(m.total)}</text>
              )}
              {label && <text className="tick" x={cx} y={H - 6} textAnchor="middle">{label}</text>}
            </g>
          )
        })}
      </svg>
    </figure>
  )
}
